import axios from "axios";
import CryptoJS from 'crypto-js';
import React, { useEffect, useState } from "react";

import styled from "styled-components";
const contactImage = "./assets/icon/images/contact-us-icon.svg";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  interface SendEmailData {
    sendEmail: {
      code: string;
      from: string;
      subject: string;
      message: string;
    };
  }

  interface EncryptedRequestBody {
    sendEmail: {
      encryptedRequest: string;
    };
  }

  interface ApiResponse {
    status: string;
    data?: any;
    message?: string;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setLoading(true);
    setError("");
    setSuccessMessage("");

    const { name, email, message } = formData;
    const secretKey = process.env.REACT_APP_CONTACT_SECRET_KEY;

    if (!secretKey) {
      setLoading(false);
      setError("Contact form is misconfigured. Please try again later.");
      return;
    }

    if (!name || !email || !message) {
      setLoading(false);
      setError("Please fill in all fields.");
      return;
    }

    if (!validateEmail(email)) {
      setLoading(false);
      setError("Invalid email address.");
      return;
    }

    if (!isChecked) {
      setLoading(false);
      setError("Please agree to the Privacy Policy and Terms of Use.");
      return;
    }

    const host = window.location.hostname;
    let params = '';

    if (host.includes("dev") || host.includes('localhost')) params = "https://dev-identity.api.infoss.solusisentraldata.com/";
    else if (host.includes("staging")) params = "https://stage-identity.api.infoss.solusisentraldata.com/";
    else params = "https://identity.api.infoss.solusisentraldata.com/";

    const originalBody: SendEmailData = {
      sendEmail: {
        code: "MLDP",
        from: email,
        subject: `The Contact By The Name of ${name} is Requesting For a Demo`,
        message: message,
      }
    };

    try {
      const jsonString = JSON.stringify(originalBody);
      const key = CryptoJS.enc.Utf8.parse(secretKey);
      const iv = CryptoJS.enc.Utf8.parse(secretKey.substring(0, 16));
      const encryptedValue = CryptoJS.AES.encrypt(jsonString, key, { iv: iv, padding: CryptoJS.pad.Pkcs7 }).toString();

      const requestBody: EncryptedRequestBody = {
        sendEmail: {
          encryptedRequest: encodeURIComponent(encryptedValue),
        },
      };

      const apiUrl = params + 'smtpSettings/smtpSettings/SendEmail';

      const response = await axios.post<ApiResponse>(apiUrl, requestBody, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });

      setSuccessMessage(response.data.message || 'Request sent successfully!');
      setFormData({ name: "", email: "", message: "" });

    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || 'An error occurred during the request.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getBaseUrl = (): string => {
    // const host = window.location.hostname;
    // if (host.includes("dev")) return "https://dev.app.majura.ai/privacy";
    // if (host.includes("staging")) return "https://staging.app.majura.ai/privacy";
    // if (host.includes("localhost")) return "https://dev.app.majura.ai/privacy";
    // return "https://app.majura.ai/privacy";
    return ''
  };

  const privacyLink = getBaseUrl();

  return (
    <Wrapper id={"contact-section"}>
      <IllustrationWrapper>
        <Illustration src={contactImage} alt="Contact Illustration" />
      </IllustrationWrapper>

      <FormWrapper>
        <Title>Get in touch with us!</Title>

        <Form>
          <Label>Name</Label>
          <Input
            type="text"
            placeholder="Enter your name"
            value={formData.name}
            name="name"
            onChange={handleChange}
          />

          <Label>Email</Label>
          <Input
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            name="email"
            onChange={handleChange}
          />

          <Label>Message</Label>
          <TextArea
            placeholder="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
          />

          <CheckboxWrapper>
            <input type="checkbox" id="terms" checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
            <label htmlFor="terms">
              I agree to <b><a href={privacyLink}>Privacy Policy</a></b> and <b>Terms of Use</b>
            </label>
          </CheckboxWrapper>

          <SubmitButton onClick={handleSubmit}>Send email to us</SubmitButton>

          {loading && <p>Loading...</p>}
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}
          {successMessage && <p style={{ color: 'green' }}>Success: {successMessage}</p>}
        </Form>
      </FormWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  display: flex;
  gap: 128px;
  margin: 8rem 2rem;
  align-items: center;
  justify-content: center;
  padding: 0 2rem 1rem;
  flex-wrap: wrap;

  @media (max-width: 1025px) {
    align-items: flex-start;
    gap: 40px;
  }

  @media (max-width: 767px) {
    margin: 4rem 0rem;
    gap: 2.5rem;
    max-width: 100%;
    flex-direction: column;
    align-items: center;
  }
`;

const IllustrationWrapper = styled.div`
  @media (min-width: 1026px) {
    flex: 1;
    max-width: 600px;
  }
`;

const Illustration = styled.img`
  width: 100%;
  height: auto;

  @media (max-width: 1025px) {
    width: 240px;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const FormWrapper = styled.div`
  flex: 1;
  max-width: 500px;

  @media (max-width: 767px) {
    max-width: 100%;
    align-items: center;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #0b1c75;
  margin-bottom: 1rem;

  @media (max-width: 1025px) {
    margin-top: 0;
  }

  @media (max-width: 767px) {
    margin: 0;
    text-align: center;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #0b1c75;
  fon-family: inherit;
  font-weight: 600;
  margin-top: 1rem !important;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 10px;
  font-size: 1rem;
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 10px;
  font-family: inherit;
  font-size: 1rem;
  resize: none;
  min-height: 120px;

  @media (max-width: 767px) {
    resize: none;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  margin-top: 1rem !important;
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  background: linear-gradient(90deg, #9d8cff, #6d94ff);
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: 0.3s ease;
  width: max-content;
  padding: 1rem 2rem;
  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 767px) {
    width: fit-content;
    border-radius: 16px;
    padding: 1rem 2rem;
  }
`;

export default ContactSection;
